import { z } from 'zod';
import { TRIP_STATUSES } from './trip.schemas';

/**
 * Handing a dispatched trip to another driver (gitaumoses4/chukua#80). The truck and trailer stay with the trip, so the body names only the
 * new driver and why. `idempotencyKey` makes a repeated request (a double click, a retry after a timeout) return the first handover.
 */
export const handoverTripInputSchema = z
  .object({
    idempotencyKey: z.string().min(1).max(200),
    newDriverId: z.uuid(),
    reason: z.string().trim().min(1, 'Say why the trip is changing drivers').max(500),
  })
  .strict();
export type HandoverTripInput = z.infer<typeof handoverTripInputSchema>;

/** The trip statuses a handover may start from: the driver is on the road or about to be. Anything earlier is dispatch's, anything later is finished by the driver. */
export const HANDOVER_TRIP_STATUSES = ['dispatched', 'in_transit', 'exception'] as const satisfies readonly (typeof TRIP_STATUSES)[number][];

/** The payload of a `handover_acknowledged` event: which handover the new driver is accepting. */
export const handoverAcknowledgementPayloadSchema = z.object({ handoverId: z.uuid() });
export type HandoverAcknowledgementPayload = z.infer<typeof handoverAcknowledgementPayloadSchema>;

/** One handover as the office reads it. Acknowledgement is derived from the new driver's `handover_acknowledged` event, never stored. */
export interface TripHandoverView {
  id: string;
  tripId: string;
  from: { id: string; name: string };
  to: { id: string; name: string };
  reason: string;
  statusAtHandover: (typeof TRIP_STATUSES)[number];
  handedOverBy: { id: string; name: string } | null;
  handedOverAt: string;
  acknowledgedAt: string | null;
}

/** The handover block the new driver's phone gets with its trip; the old driver's name is enough, the rest is the office's. */
export const syncHandoverViewSchema = z.object({
  id: z.uuid(),
  fromDriverName: z.string(),
  reason: z.string(),
  handedOverAt: z.iso.datetime({ offset: true }),
  isAcknowledged: z.boolean(),
});
export type SyncHandoverView = z.infer<typeof syncHandoverViewSchema>;
